import { LOCALES, type MailTemplateSummary } from '@recipes/contracts';
import { Alert, Badge, Spinner } from '@recipes/ui';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { AdminPage } from '@/features/admin/admin-layout';
import { formatDate } from '@/features/shared/labels';
import { ResponsiveTable } from '@/features/shared/responsive-table';
import { api } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

/** Admin → E-mail → Templates: every mail in every language, default or customized. */
export function AdminMailTemplatesPage() {
  const { t, i18n } = useTranslation();
  const templates = useQuery({
    queryKey: queryKeys.admin.mailTemplates,
    queryFn: () => api.admin.mail.templates(),
  });
  return (
    <AdminPage section="mailTemplates" description={t('admin.mailTemplates.description')}>
      {templates.isLoading && <Spinner label={t('app.loading')} />}
      {templates.error && <Alert variant="destructive">{describeError(templates.error)}</Alert>}
      {templates.data && (
        <ResponsiveTable<MailTemplateSummary>
          caption={t('admin.sections.mailTemplates')}
          rows={templates.data}
          rowKey={(row) => `${row.key}/${row.locale}`}
          columns={[
            {
              key: 'name',
              header: t('admin.mailTemplates.template'),
              cell: (row) => (
                <Link
                  to={`/admin/mail/templates/${row.key}/${row.locale}`}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                  data-testid={`mail-template-${row.key}-${row.locale}`}
                >
                  {t(`admin.mailTemplates.names.${row.key}`)}
                </Link>
              ),
            },
            {
              key: 'locale',
              header: t('admin.mailTemplates.language'),
              cell: (row) => LOCALES[row.locale].nativeName,
            },
            {
              key: 'subject',
              header: t('admin.mailTemplates.subject'),
              cell: (row) => row.subject,
            },
            {
              key: 'sender',
              header: t('admin.mailTemplates.sender'),
              cell: (row) => t(`admin.mailTemplates.purpose.${row.purpose}`),
            },
            {
              key: 'state',
              header: t('admin.mailTemplates.state'),
              cell: (row) =>
                row.customized ? (
                  <Badge>{t('admin.mailTemplates.customized', { version: row.version })}</Badge>
                ) : (
                  <Badge variant="outline">{t('admin.mailTemplates.default')}</Badge>
                ),
            },
            {
              key: 'changed',
              header: t('admin.mailTemplates.changed'),
              cell: (row) =>
                row.updatedAt
                  ? `${formatDate(row.updatedAt, i18n.language, true)}${
                      row.updatedByName ? ` · ${row.updatedByName}` : ''
                    }`
                  : '–',
            },
          ]}
        />
      )}
    </AdminPage>
  );
}
